import { useAIWorkspace } from "@/hooks/useAIWorkspace";
import { useReplaySeek } from "@/hooks/useReplaySeek";
import { useState, useEffect } from "react";
import api, { pollTask, API } from "@/lib/api";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { TrustBadge, ScoreRing, CompetencyBar } from "@/components/shared";
import { toast } from "sonner";
import { Quote, ShieldCheck, UserCheck, Flag, Info, ThumbsUp, ThumbsDown, Sparkles, Loader2, MessageCircleQuestion, ClipboardList, FileText, Video, MonitorPlay } from "lucide-react";
import { ReplayPlayer } from "@/components/ReplayPlayer";
import { ProctorIdentityBlock } from "@/components/ProctorIdentityBlock";
import { EmployerProfileDrawer } from "@/components/EmployerProfileDrawer";
import { EvidenceLightbox } from "@/components/proctor/EvidenceLightbox";
import { VoiceSignal } from "@/components/proctor/VoiceSignal";
import { useSignedFiles } from "@/hooks/useSignedFiles";

const SEV_BADGE = { high: "flag", medium: "medium", low: "high" }; // low renders emerald (benign)
const DIGEST_UI = {
  review: { wrap: "border-amber-500/40 bg-amber-500/10", icon: "text-amber-400", title: "text-amber-300", label: "Human review suggested" },
  minor: { wrap: "border-sky-500/30 bg-sky-500/10", icon: "text-sky-400", title: "text-sky-300", label: "Minor signals — likely fine" },
  clean: { wrap: "border-emerald-500/30 bg-emerald-500/10", icon: "text-emerald-400", title: "text-emerald-300", label: "Looks clean" },
};

export function EvidenceDrawer({ candidate, onClose, onUpdated }) {
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [revising, setRevising] = useState(false);
  const [questions, setQuestions] = useState(null);
  const [copiloting, setCopiloting] = useState(false);
  const [kit, setKit] = useState(null);
  const [kitLoading, setKitLoading] = useState(false);
  const [transcript, setTranscript] = useState("");
  const [analysis, setAnalysis] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [proctor, setProctor] = useState(null);
  const { seek, replayRef, cameraRef, screenRef, seekNotice, jump, canSeek } = useReplaySeek(proctor);
  const [showCvProfile, setShowCvProfile] = useState(false);
  const [lbIndex, setLbIndex] = useState(null);
  const open = !!candidate;
  const c = candidate;
  const token = localStorage.getItem("hs_token");
  const evidence = (proctor?.violations ? [...proctor.violations].reverse() : []).filter((v) => v.snapshot_path);
  const openEvidence = (v) => { const idx = evidence.findIndex((e) => (e.id || e) === (v.id || v)); setLbIndex(idx >= 0 ? idx : 0); };
  const signUrl = useSignedFiles([proctor?.recording?.path, proctor?.screen_recording?.path,
    ...((proctor?.violations || []).map((v) => v.snapshot_path)),
    ...((proctor?.snapshots || []).map((s) => s.path))].filter(Boolean));

  useEffect(() => {
    setQuestions(null);
    setCopiloting(false);
    setRevising(false);
    setNote("");
    setKit(candidate?.interview?.kit || null);
    setAnalysis(candidate?.interview?.analysis || null);
    setTranscript("");
    setProctor(null);
    if (candidate?.id) {
      api.get(`/candidates/${candidate.id}/proctor`).then(({ data }) => setProctor(data)).catch(() => setProctor(null));
    }
  }, [candidate?.id, candidate?.interview?.kit, candidate?.interview?.analysis]);

  const genKit = async () => {
    setKitLoading(true);
    try { const { data: r } = await api.post(`/candidates/${c.id}/interview-kit`); const kitData = await pollTask(r.task_id); setKit(kitData); }
    catch (e) { toast.error(e.message || "Could not generate kit"); }
    finally { setKitLoading(false); }
  };

  const analyze = async () => {
    if (!transcript.trim()) { toast.error("Paste an interview transcript first"); return; }
    setAnalyzing(true);
    setAnalysis(null);
    try { const { data: r } = await api.post(`/candidates/${c.id}/interview-analyze`, { transcript }); const a = await pollTask(r.task_id); setAnalysis(a); toast.success("Transcript analyzed with cited evidence"); }
    catch (e) { toast.error(e.message || "Could not analyze transcript"); }
    finally { setAnalyzing(false); }
  };

  const openChat = useAIWorkspace();
  const loadCopilot = () => openChat("Suggest fair, evidence-grounded interview questions for this candidate, with a reason for each question.", { mode: "Interview Copilot", context: {candidate_id:c.id}, previewUrl:`/recruiter/candidate/${c.id}` });

  const override = async (decision) => {
    if (!note.trim()) { toast.error("Add a note explaining your decision"); return; }
    setSaving(true);
    try {
      const { data } = await api.post(`/candidates/${c.id}/override`, { decision, note });
      toast.success("Human override logged to audit trail");
      onUpdated?.({ ...c, human_review: data.human_review });
      setNote("");
      setRevising(false);
    } catch { toast.error("Could not save override"); }
    finally { setSaving(false); }
  };

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto p-0" data-testid="evidence-drawer">
        {c && (
          <div className="p-6 space-y-6">
            <SheetHeader className="space-y-0">
              <div className="flex items-center gap-3">
                {c.avatar ? <img src={c.avatar} alt={c.name} loading="lazy" decoding="async" className="h-14 w-14 rounded-full object-cover" />
                  : <div className="h-14 w-14 rounded-full bg-gradient-to-br from-[#0E7C82] to-[#15B3BC] flex items-center justify-center text-white text-lg font-semibold">{c.name[0]}</div>}
                <div className="flex-1 text-left">
                  <SheetTitle className="text-xl font-display">{c.name}</SheetTitle>
                  <p className="text-sm text-muted-foreground">
                    {c.parsed_profile?.headline && c.parsed_profile.headline !== c.name ? `${c.parsed_profile.headline} · ` : ""}{c.email}
                  </p>
                </div>
                <ScoreRing score={c.overall_score} size={60} label="score" />
              </div>
            </SheetHeader>
            <SheetDescription className="sr-only">Candidate evidence, proctoring signals, and human review</SheetDescription>

            <div className="flex flex-wrap gap-2">
              {c.confidence && <TrustBadge type={c.confidence === "high" ? "high" : "medium"}>{c.confidence} confidence</TrustBadge>}
              {c.human_review && <TrustBadge type="override"><UserCheck className="h-3 w-3" />human reviewed</TrustBadge>}
              {c.violations?.length > 0 && <TrustBadge type="flag"><Flag className="h-3 w-3" />{c.violations.length} advisory flag(s)</TrustBadge>}
              <button onClick={() => setShowCvProfile(true)} data-testid="evidence-view-cv-profile" className="ml-auto text-xs text-primary hover:underline inline-flex items-center gap-1"><FileText className="h-3 w-3" /> Full CV profile</button>
              <a href={`/recruiter/candidate/${c.id}`} className="text-xs text-primary hover:underline inline-flex items-center gap-1" data-testid="open-full-intelligence">Full intelligence →</a>
            </div>

            {showCvProfile && <EmployerProfileDrawer candidateId={c.id} onClose={() => setShowCvProfile(false)} />}

            <Tabs defaultValue="explain">
              <TabsList className="w-full grid grid-cols-5">
                <TabsTrigger value="explain" data-testid="tab-explain">Why</TabsTrigger>
                <TabsTrigger value="evidence" data-testid="tab-evidence">Evidence</TabsTrigger>
                <TabsTrigger value="proctoring" data-testid="tab-proctoring">Proctor</TabsTrigger>
                <TabsTrigger value="copilot" data-testid="tab-copilot">Copilot</TabsTrigger>
                <TabsTrigger value="interview" data-testid="tab-interview">Interview</TabsTrigger>
              </TabsList>

              <TabsContent value="explain" className="space-y-5 pt-4">
                <div className="space-y-4">
                  {c.competencies?.map((comp, i) => <CompetencyBar key={i} {...comp} />)}
                </div>
                <div className="rounded-xl border border-border bg-card p-4 space-y-2">
                  <div className="flex items-center gap-2 text-sm font-semibold"><ShieldCheck className="h-4 w-4 text-primary" /> AI rationale</div>
                  <p className="text-sm text-muted-foreground leading-relaxed">{c.rationale || "No rationale available."}</p>
                </div>
              </TabsContent>

              <TabsContent value="evidence" className="space-y-3 pt-4">
                {c.evidence?.length ? c.evidence.map((e, i) => (
                  <div key={i} className="p-3 rounded-lg bg-amber-500/10 border-l-4 border-amber-500 space-y-1.5" data-testid={`evidence-${i}`}>
                    <div className="flex items-start gap-2">
                      <Quote className="h-3.5 w-3.5 text-amber-400 mt-0.5 shrink-0" />
                      <p className="text-sm text-amber-100 font-mono leading-relaxed">"{e.quote}"</p>
                    </div>
                    <div className="flex items-center justify-between pl-5">
                      <span className="text-[11px] text-amber-400/80">{e.source}</span>
                      <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-300">{e.tag}</span>
                    </div>
                  </div>
                )) : <p className="text-sm text-muted-foreground">No evidence captured.</p>}
              </TabsContent>

              <TabsContent value="proctoring" className="space-y-3 pt-4">
                <div className="rounded-lg border border-blue-500/30 bg-blue-500/10 p-3 flex gap-2.5">
                  <Info className="h-4 w-4 text-blue-400 shrink-0 mt-0.5" />
                  <p className="text-xs text-blue-700 dark:text-blue-200 leading-relaxed">Proctoring signals are <strong>advisory only</strong> and never proof of misconduct or identity. Use them only to decide whether a human review is warranted.</p>
                </div>

                {proctor?.digest && (() => { const u = DIGEST_UI[proctor.digest.verdict] || DIGEST_UI.minor; return (
                  <div className={`rounded-lg border p-3 flex items-start gap-2 ${u.wrap}`} data-testid="proctor-digest">
                    <Sparkles className={`h-4 w-4 shrink-0 mt-0.5 ${u.icon}`} />
                    <div>
                      <div className={`text-xs font-semibold ${u.title}`}>{u.label}</div>
                      <p className="text-xs text-muted-foreground leading-relaxed">{proctor.digest.summary}</p>
                    </div>
                  </div>); })()}

                {proctor?.voice_signal && <VoiceSignal signal={proctor.voice_signal} candId={c?.id} recruiter />}

                {proctor?.recording?.path && (
                  <div className="space-y-2" data-testid="proctor-recording">
                    <div className="text-[11px] uppercase tracking-wider text-muted-foreground flex items-center gap-1.5"><Video className="h-3.5 w-3.5" /> Full session recording</div>
                    <video ref={cameraRef} controls src={signUrl(proctor.recording.path)} className="w-full rounded-xl border border-white/10 bg-black" data-testid="recording-video" />
                  </div>
                )}

                {proctor?.screen_recording?.path && (
                  <div className="space-y-2" data-testid="proctor-screen-recording">
                    <div className="text-[11px] uppercase tracking-wider text-muted-foreground flex items-center gap-1.5"><MonitorPlay className="h-3.5 w-3.5" /> Screen recording</div>
                    <video ref={screenRef} controls src={signUrl(proctor.screen_recording.path)} className="w-full rounded-xl border border-white/10 bg-black" data-testid="screen-recording-video" />
                  </div>
                )}

                {proctor?.identity && <ProctorIdentityBlock candId={c?.id} identity={proctor.identity} apiBase={API} token={token} />}

                {proctor?.snapshots?.length > 0 && (
                  <div ref={replayRef}>
                    <ReplayPlayer frames={proctor.snapshots} apiBase={API} token={token} seekTo={seek.idx} seekNonce={seek.nonce} />
                  </div>
                )}

                {proctor && proctor.total > 0 && (
                  <div className="grid grid-cols-3 gap-2" data-testid="proctor-severity">
                    {["high", "medium", "low"].map((s) => (
                      <div key={s} className="rounded-lg border border-border bg-card p-2 text-center">
                        <div className={`text-lg font-mono font-bold ${s === "high" ? "text-rose-400" : s === "medium" ? "text-amber-400" : "text-emerald-400"}`}>{proctor.severity_counts?.[s] || 0}</div>
                        <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{s}</div>
                      </div>
                    ))}
                  </div>
                )}

                {proctor?.consent?.alternative_path && (
                  <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 p-2.5 text-xs text-emerald-200" data-testid="proctor-nonbiometric">Candidate chose the non-biometric path — no camera signals were collected.</div>
                )}

                {seekNotice && <p role="status" data-testid="drawer-replay-seek-notice" className="text-xs text-primary">{seekNotice}</p>}
                {proctor?.violations?.length ? [...proctor.violations].reverse().map((v, i) => (
                  <div key={v.id || i} role="button" tabIndex={0} onKeyDown={(e)=>{if(e.key==='Enter'||e.key===' '){e.preventDefault();jump(v);}}} onClick={() => jump(v)} className={`p-3 rounded-lg border border-border bg-card space-y-2 ${canSeek(v) ? "cursor-pointer hover:border-primary/50 transition-colors" : ""}`} data-testid={`violation-${i}`}>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">{v.label || v.type.replace(/_/g, " ")}</span>
                      <TrustBadge type={SEV_BADGE[v.severity] || "medium"}>{v.severity}</TrustBadge>
                    </div>
                    {v.detail && <p className="text-xs text-muted-foreground">{v.detail}</p>}
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] font-mono text-muted-foreground">{v.at ? new Date(v.at).toLocaleTimeString() : ""}</span>
                      <span className="text-[10px] text-primary" data-testid={`drawer-moment-label-${i}`}>{canSeek(v) ? "Jump to moment →" : "No matching recording"}</span>
                      {v.snapshot_path && (
                        <button onClick={(e) => { e.stopPropagation(); openEvidence(v); }} className="shrink-0 rounded border border-white/10 overflow-hidden hover:ring-2 hover:ring-primary/50 transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary" data-testid={`violation-snap-${i}`} aria-label="Open evidence image">
                          <img src={signUrl(v.snapshot_path)} alt="evidence frame" className="h-12 w-16 object-cover" />
                        </button>
                      )}
                    </div>
                  </div>
                )) : <p className="text-sm text-muted-foreground" data-testid="proctor-empty">No proctoring flags. Clean session.</p>}

                {proctor?.snapshots?.length > 0 && (
                  <div className="space-y-2 pt-1">
                    <div className="text-[11px] uppercase tracking-wider text-muted-foreground">Session snapshots</div>
                    <div className="grid grid-cols-4 gap-2" data-testid="proctor-snapshots">
                      {proctor.snapshots.slice(-8).map((s, i) => (
                        <img key={s.id || i} src={signUrl(s.path)} alt="session frame" className="aspect-video object-cover rounded border border-white/10" />
                      ))}
                    </div>
                  </div>
                )}
              </TabsContent>

              <TabsContent value="copilot" className="space-y-3 pt-4" data-testid="copilot-panel">
                <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 flex gap-2.5">
                  <Sparkles className="h-4 w-4 text-amber-400 shrink-0 mt-0.5" />
                  <p className="text-xs text-amber-200 leading-relaxed">Interview copilot suggests fair, job-relevant follow-up questions that probe this candidate's gaps and verify their strongest claims.</p>
                </div>
                <Button size="sm" className="w-full gap-1.5" onClick={loadCopilot} disabled={copiloting} data-testid="copilot-generate">
                  {copiloting ? <><Loader2 className="h-4 w-4 animate-spin" /> Thinking…</> : <><MessageCircleQuestion className="h-4 w-4" /> {questions ? "Regenerate questions" : "Suggest interview questions"}</>}
                </Button>
                {questions?.map((q, i) => (
                  <div key={i} className="p-3 rounded-lg border border-border bg-card space-y-1" data-testid={`copilot-q-${i}`}>
                    <p className="text-sm font-medium">{i + 1}. {q.q}</p>
                    {q.why && <p className="text-xs text-muted-foreground">Tests: {q.why}</p>}
                  </div>
                ))}
              </TabsContent>

              <TabsContent value="interview" className="space-y-4 pt-4" data-testid="interview-panel">
                <div className="rounded-xl border border-border bg-card p-3 space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="text-sm font-semibold flex items-center gap-1.5"><ClipboardList className="h-4 w-4 text-primary" /> Structured interview kit</div>
                    <Button size="sm" variant="outline" onClick={genKit} disabled={kitLoading} data-testid="gen-kit">
                      {kitLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : (kit ? "Regenerate" : "Generate kit")}
                    </Button>
                  </div>
                  {kit ? kit.competencies?.map((comp, i) => (
                    <div key={i} className="rounded-lg bg-muted/40 p-2.5" data-testid={`kit-comp-${i}`}>
                      <div className="text-xs font-medium text-primary mb-1">{comp.name}</div>
                      <ul className="space-y-1">{comp.questions?.map((q, k) => <li key={k} className="text-xs text-foreground/90 flex gap-1.5"><span className="text-muted-foreground">{k + 1}.</span>{q}</li>)}</ul>
                    </div>
                  )) : <p className="text-xs text-muted-foreground">Generate a competency-mapped question set for this candidate.</p>}
                </div>

                <div className="rounded-xl border border-border bg-card p-3 space-y-3">
                  <div className="text-sm font-semibold flex items-center gap-1.5"><FileText className="h-4 w-4 text-primary" /> Transcript analysis</div>
                  <Textarea rows={4} value={transcript} onChange={(e) => { setTranscript(e.target.value); if (analysis) setAnalysis(null); }} placeholder="Paste the interview transcript here…" data-testid="transcript-input" />
                  <Button size="sm" className="w-full gap-1.5" onClick={analyze} disabled={analyzing} data-testid="analyze-transcript">
                    {analyzing ? <><Loader2 className="h-4 w-4 animate-spin" /> Analyzing…</> : "Analyze with cited evidence"}
                  </Button>
                  <div className="rounded-lg border border-blue-500/30 bg-blue-500/10 p-2.5 flex gap-2"><Info className="h-3.5 w-3.5 text-blue-400 shrink-0 mt-0.5" /><p className="text-[11px] text-blue-200">Evidence-grounded only — no emotion or personality inference. AI recommends; you decide.</p></div>
                  {analysis && (
                    <div className="space-y-3 pt-1" data-testid="transcript-analysis">
                      {analysis.thin && <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-2.5 text-[11px] text-amber-200" data-testid="thin-warning">Thin transcript ({analysis.word_count} words) — confidence capped to low. Add more content for a reliable read.</div>}
                      <div className="flex items-center gap-2"><span className="text-xs text-muted-foreground">Suggested:</span><TrustBadge type={analysis.recommendation === "advance" ? "high" : analysis.recommendation === "decline" ? "flag" : "medium"}>{analysis.recommendation}</TrustBadge></div>
                      <p className="text-xs text-muted-foreground leading-relaxed">{analysis.summary}</p>
                      {analysis.competencies?.map((cp, i) => (
                        <div key={i} className="rounded-lg bg-muted/40 p-2.5 space-y-1.5" data-testid={`analysis-comp-${i}`}>
                          <div className="flex items-center justify-between text-xs"><span className="font-medium">{cp.name}</span><span className="flex items-center gap-1.5"><TrustBadge type={cp.confidence === "high" ? "high" : "medium"}>{cp.confidence}</TrustBadge><span className="font-mono text-primary">{cp.score}</span></span></div>
                          {cp.excerpts?.map((ex, k) => (
                            <div key={k} className="border-l-2 border-amber-500 pl-2"><p className="text-[11px] text-amber-100 font-mono">"{ex.quote}"</p><p className="text-[10px] text-muted-foreground">{ex.note}</p></div>
                          ))}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </TabsContent>
            </Tabs>

            {/* Human decision */}
            <div className="rounded-xl border border-indigo-500/30 bg-indigo-500/5 p-4 space-y-3">
              <div className="flex items-center gap-2 text-sm font-semibold"><UserCheck className="h-4 w-4 text-indigo-400" /> Human decision {" "}<span className="text-xs font-normal text-muted-foreground">(required)</span></div>
              {c.human_review && !revising ? (
                <div className="text-sm space-y-2">
                  <div className="flex items-center gap-2"><TrustBadge type="override">{c.human_review.decision}</TrustBadge><span className="text-xs text-muted-foreground">by {c.human_review.reviewer}</span></div>
                  <p className="text-muted-foreground text-xs pt-1">"{c.human_review.note}"</p>
                  <Button size="sm" variant="outline" onClick={() => setRevising(true)} data-testid="revise-decision">Revise decision</Button>
                </div>
              ) : (
                <>
                  <Label htmlFor="ov-note" className="text-xs">Rationale for your decision</Label>
                  <Textarea id="ov-note" value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="Explain your reasoning — this is logged to the audit trail." data-testid="override-note" />
                  <div className="flex gap-2">
                    <Button size="sm" className="flex-1 gap-1.5" onClick={() => override("advance")} disabled={saving} data-testid="override-advance"><ThumbsUp className="h-3.5 w-3.5" /> Advance</Button>
                    <Button size="sm" variant="outline" className="flex-1 gap-1.5" onClick={() => override("reject")} disabled={saving} data-testid="override-reject"><ThumbsDown className="h-3.5 w-3.5" /> Reject</Button>
                  </div>
                </>
              )}
            </div>
            <EvidenceLightbox items={evidence} index={lbIndex} onIndexChange={setLbIndex} onClose={() => setLbIndex(null)} apiBase={API} token={token} />
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
